import React, { useRef, useState } from "react";
import { ArrowLeft, LockKeyhole, Shuffle } from "lucide-react";
import RobustAssessmentProfileView from "@/components/assessment/RobustAssessmentProfileView";
import RobustAssessmentPdfButton from "@/components/assessment/RobustAssessmentPdfButton";
import {
  createQuickAssessmentSeed,
  generateQuickAssessmentPreview,
} from "@/components/admin/assessment/randomizedAssessmentPreview";

const QuickAssessmentResults = ({ onBack }) => {
  const [preview, setPreview] = useState(() => generateQuickAssessmentPreview());
  const topRef = useRef(null);

  const randomize = () => {
    const seed = createQuickAssessmentSeed(preview.seed);
    setPreview(
      generateQuickAssessmentPreview({
        seed,
        excludeScenarioId: preview.scenarioId,
      }),
    );
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="quick-assessment-results" ref={topRef} className="scroll-mt-24">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm font-medium forr-text-teal-deep"
      >
        <ArrowLeft size={16} /> Return to APOS Admin
      </button>

      <section
        className="mt-5 rounded-[28px] p-6 forr-surface sm:p-8"
        style={{ border: "1px solid var(--forr-line)" }}
      >
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="forr-overline">Owner testing tool</div>
            <h1 className="mt-3 font-display text-4xl tracking-[-0.035em] forr-text-ink sm:text-5xl">
              Quick assessment results
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed forr-text-soft sm:text-base">
              Generate a coherent set of Version 5 answers and run it through the real APOS results engine. Use this to read and compare result variations without completing the assessment form.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <RobustAssessmentPdfButton
              answers={preview.answers}
              profile={preview.profile}
              filename={`apos-quick-test-result-${preview.seed}`}
              label="Download test PDF"
              className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 text-sm font-semibold forr-btn-secondary disabled:opacity-50"
            />
            <button
              type="button"
              onClick={randomize}
              className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold forr-btn-primary"
            >
              <Shuffle size={17} /> Randomize results
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl p-4 forr-panel-information sm:col-span-1">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] forr-text-teal-deep">
              <LockKeyhole size={14} /> Preview boundary
            </div>
            <p className="mt-2 text-xs leading-relaxed forr-text-soft">
              Temporary test only. Nothing is saved to assessment history, your active baseline, the cloud profile, or participant records.
            </p>
          </div>
          <div className="rounded-2xl p-4 forr-surface-tint">
            <div className="text-[10px] uppercase tracking-[0.15em] forr-text-mute">
              Result pattern
            </div>
            <div className="mt-2 text-sm font-medium forr-text-ink">
              {preview.scenarioLabel}
            </div>
          </div>
          <div className="rounded-2xl p-4 forr-surface-tint">
            <div className="text-[10px] uppercase tracking-[0.15em] forr-text-mute">
              Reproducible seed
            </div>
            <div className="mt-2 font-mono text-sm forr-text-ink">{preview.seed}</div>
          </div>
        </div>
      </section>

      <div className="mt-6 rounded-[28px] p-1">
        <RobustAssessmentProfileView
          answers={preview.answers}
          profile={preview.profile}
        />
      </div>

      <div
        className="mt-7 flex flex-col gap-4 rounded-[24px] p-5 forr-panel-action sm:flex-row sm:items-center sm:justify-between sm:p-6"
      >
        <div>
          <div className="forr-overline">Finished reading this variation?</div>
          <p className="mt-2 text-sm leading-relaxed forr-text-soft">
            Generate another temporary profile. Your real APOS assessment and Companion remain unchanged.
          </p>
        </div>
        <button
          type="button"
          onClick={randomize}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold forr-btn-primary"
        >
          <Shuffle size={16} /> Randomize another result
        </button>
      </div>
    </div>
  );
};

export default QuickAssessmentResults;
